import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {AlertCircle, X} from 'lucide-react'

const Problem = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  const problems = [
    'Estuda sozinha',
    'Sem estrutura',
    'Sem apoio',
    'Sem alguém puxando'
  ]

  return (
    <section ref={ref} className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-0 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Asymmetric layout */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12 items-center">
          {/* Left side - Icon */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="md:col-span-2 flex justify-center"
          >
            <div className="relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-br from-red-400 to-orange-400 rounded-full blur-2xl opacity-20"
              ></motion.div>
              <div className="relative w-48 h-48 md:w-64 md:h-64 bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl flex items-center justify-center shadow-2xl">
                <AlertCircle className="w-24 h-24 md:w-32 md:h-32 text-red-500" strokeWidth={1.5} />
              </div>
            </div>
          </motion.div>

          {/* Right side - Content */}
          <div className="md:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
                Se aprender inglês fosse só{' '}
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  questão de disciplina
                </span>
                , você já estaria fluente
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="space-y-4 mb-8"
            >
              <p className="text-xl md:text-2xl text-gray-700 font-medium leading-relaxed">
                A maioria das pessoas <span className="font-bold text-gray-900">não desiste por falta de capacidade</span>.
              </p>
              
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Desiste porque:
              </p>
            </motion.div>

            {/* Problem cards */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="bg-white border-2 border-red-200 rounded-xl p-4 flex items-start gap-3 hover:border-red-400 transition-colors duration-300"
                >
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <X className="w-4 h-4 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-gray-900 font-semibold">{problem}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
              className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 border-l-4 border-[#C64DAD]"
            >
              <p className="text-xl md:text-2xl text-white font-bold leading-relaxed">
                Não é falta de inteligência.
                <br />
                É falta de um <span className="text-yellow-300">ambiente que sustente o processo</span>.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Problem
